import { useMemo, useState } from 'react'
import { countdown, price as fmtPrice } from '../format'
import { buildLadder } from '../ladder'
import { CompactLadder } from './CompactLadder'
import { DepthChart } from './DepthChart'
import { FillsPanel } from './FillsPanel'
import { NewOrderForm } from './NewOrderForm'
import { OrdersTab } from './OrdersTab'
import { TradeChart } from './TradeChart'
import { TradeTape } from './TradeTape'
import type { MarketState } from '../useMarketSocket'

/**
 * İşlem ekranı — YALNIZ mobil. Masaüstünde karşılığı yok: orada kontrat
 * listesi, emir defteri, grafik ve eşleşmeler zaten 2×2 ızgarada açık.
 *
 *   ┌──────────────────────────┐
 *   │ kontrat · sayaç · PTF    │  yapışkan başlık
 *   ├─────────────┬────────────┤
 *   │ emir formu  │ defter     │  fiyata dokun → forma yazılır
 *   ├─────────────┴────────────┤
 *   │ Grafik Derinlik Eşleş... │  segment şeridi (yatay kaydırmalı)
 *   │ (seçilen segmentin       │
 *   │  gövdesi, kendi içinde   │
 *   │  kayar)                  │
 *   └──────────────────────────┘
 *
 * Form ve defterin yan yana durması borsaların mobil deseni: fiyat girerken
 * tahtayı görmek gerekiyor, alt alta dizilseydi biri ekrandan çıkardı.
 *
 * Botlar burada DEĞİL: kendi alt sekmesinde (`BotsPage`) ve orada tüm
 * kontratların botlarını birden gösteriyor — telefondaki iş kontrat kontrat
 * gezinmek değil, hepsini bir bakışta görmek.
 *
 * Yeni bileşen yalnız başlık, kompakt defter ve şerit; segment gövdeleri
 * masaüstündeki bileşenlerin ta kendisi (hepsi `.panel` içinde yaşamak üzere
 * yazıldığı için esneklik özelliklerini kendileri taşıyor).
 */

type SegmentId = 'grafik' | 'derinlik' | 'eslesme' | 'islem' | 'emirler'

const SEGMENTLER: { id: SegmentId; label: string }[] = [
  { id: 'grafik', label: 'Grafik' },
  // Derinlik grafiği ayrı bir SEGMENT, `Grafik`in içinde alt anahtar değil
  // (kullanıcı isteği 2026-08-03): şeridin içinde tek dokunuşla geçiliyor ve
  // dikeyden bir piksel bile götürmüyor. Gövde zaten dar — CompactLadder'ın
  // 4 seviyede tutulma sebebi de bu.
  { id: 'derinlik', label: 'Derinlik' },
  { id: 'eslesme', label: 'Eşleşmeler' },
  { id: 'islem', label: 'İşlemlerim' },
  { id: 'emirler', label: 'Emirlerim' },
]

interface Props {
  state: MarketState
  now: number
}

export function TradePage({ state, now }: Props) {
  const [segment, setSegment] = useState<SegmentId>('grafik')
  // Deftere her dokunuşta `sira` artıyor — forma "yeni öneri geldi" diyen şey
  // değerin kendisi değil bu sayaç (aynı fiyata tekrar dokunmak da çalışsın).
  const [oneri, setOneri] = useState<{ fiyat: number; sira: number } | null>(null)

  const secili = state.selected
  const ladder = useMemo(
    () => buildLadder(state.depth, state.orders),
    [state.depth, state.orders],
  )

  // Kancalardan SONRA erken dönüş — sıra bozulursa React uyarır.
  if (!secili) {
    return (
      <main className="page-empty">
        <div className="page-empty-card">
          <h2>Kontrat seçilmedi</h2>
          <p>Piyasa sekmesinden bir kontrata dokun; işlem ekranı burada açılır.</p>
        </div>
      </main>
    )
  }

  const kontrat = state.contracts.find((c) => c.key === secili)
  const kapali = kontrat?.closed ?? false
  const ticker = state.board[secili]
  const kalan = kontrat?.closesAt ? new Date(kontrat.closesAt).getTime() - now : null

  return (
    <main className="trade-page">
      {/* Kontrat kimliği ve fiyat her an görünür kalıyor: segmentler arasında
          gezinirken "hangi kontrattayım" sorusu sorulmasın. */}
      <header className="trade-head">
        <div className="th-ust">
          <span className="th-ad">
            <span className={`status-dot${kapali ? ' closed' : ''}`} aria-hidden="true" />
            {secili.slice(0, -2)}
            <b>{secili.slice(-2)}</b>
          </span>
          <span className="th-sayac">
            {kapali ? 'kapandı' : kalan != null && kalan > 0 ? countdown(kalan) : '—'}
          </span>
        </div>
        <div className="th-alt">
          <span className="th-deger">
            <em>PTF</em>
            <b className="num">{fmtPrice(ticker?.mcp)}</b>
          </span>
          <span className="th-deger bid">
            <em>Alış</em>
            <b className="num">{fmtPrice(ticker?.bestBidPrice)}</b>
          </span>
          <span className="th-deger ask">
            <em>Satış</em>
            <b className="num">{fmtPrice(ticker?.bestAskPrice)}</b>
          </span>
        </div>
      </header>

      {kapali ? (
        <div className="trade-kapali">
          Bu kontrat kapandı — tahta yok, yeni emir girilemez. Geçmişi aşağıdaki
          sekmelerden inceleyebilirsin.
        </div>
      ) : (
        <div className="trade-emir">
          <div className="trade-form">
            <NewOrderForm contract={secili} depth={state.depth} oneri={oneri} />
          </div>
          <CompactLadder
            ladder={ladder}
            hasDepth={state.depth != null}
            // Taraf başına 4: beşincisi bloğu 350 piksele çıkarıp altındaki
            // segment gövdesini (grafiğin çizildiği yer) 211 piksele düşürüyordu.
            levels={4}
            onPrice={(fiyat) => setOneri((onceki) => ({ fiyat, sira: (onceki?.sira ?? 0) + 1 }))}
          />
        </div>
      )}

      <div className="trade-seg" role="tablist" aria-label="İşlem ayrıntısı">
        {SEGMENTLER.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={segment === item.id}
            className={`trade-seg-btn${segment === item.id ? ' active' : ''}`}
            onClick={() => setSegment(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="trade-govde">
        {segment === 'grafik' && (
          <TradeChart
            trades={state.trades}
            fills={state.fills}
            contract={secili}
            mcp={ticker?.mcp ?? null}
          />
        )}
        {/* `DepthChart` gövde döndürüyor (efsane + çizim kutusu), kendi kabuğu
            yok — masaüstünde `DepthPanel`in sekmesi olarak yaşıyor. Buradaki
            `.panel` o zinciri kuruyor: `.chart-box` yüksekliğini ondan alıyor,
            çerçevesini de `.trade-govde > .panel` kuralı söküyor. */}
        {segment === 'derinlik' && (
          <div className="panel">
            <DepthChart depth={state.depth} mcp={ticker?.mcp ?? null} />
          </div>
        )}
        {segment === 'eslesme' && <TradeTape trades={state.trades} fills={state.fills} />}
        {segment === 'islem' && <FillsPanel contract={secili} fills={state.fills} />}
        {segment === 'emirler' && (
          <OrdersTab
            orders={state.orders}
            contract={secili}
            closed={kapali}
            depth={state.depth}
            formGoster={false}
          />
        )}
      </div>
    </main>
  )
}
